import React from 'react';
import MaterialIcon from '@/components/MaterialIcon';
import SmartLink from '@/components/SmartLink';

interface CoreFeaturesSectionProps {
  onOpenVideo?: (videoSrc: string) => void;
}

/**
 * コア3機能で業務を革新 Section
 *
 * 3つのコア機能を紹介（順序変更）:
 * 1. FAQ・施設案内AI (動画左、テキスト右)
 * 2. フロアマップ・ナビゲーション (テキスト左、動画右)
 * 3. リアルタイム会話 (動画左、テキスト右)
 *
 * 各機能に実際の体験ボタンを配置
 */
export const CoreFeaturesSection: React.FC<CoreFeaturesSectionProps> = ({ onOpenVideo }) => {
  const handleVideoClick = (videoSrc: string) => {
    if (onOpenVideo) {
      onOpenVideo(videoSrc);
    }
  };

  return (
    <section
      id="core-features"
      className="w-full px-4 md:px-10 lg:px-40 py-20 bg-white border-y border-solution-border"
    >
      <div className="max-w-[1200px] w-full mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-solution-orange font-bold tracking-wider uppercase text-sm mb-2 block">
            AIチャットボットの主要機能 - フロアマップ連携・多言語対応・リアルタイム会
          </span>
          <h2 className="text-3xl md:text-4xl font-black text-solution-text mb-4">
            コア<span className="text-solution-orange">3</span>機能で業務を革新
          </h2>
          <p className="text-solution-text-secondary text-lg">地図・FAQ・リアルタイム会話で顧客体験3倍向上</p>
        </div>

        <div className="space-y-16">
          {/* 機能1: FAQ・施設案内AI（動画左、テキスト右） */}
          <div id="feature-faq" className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center scroll-mt-20">
            {/* Video */}
            <div className="order-1 flex flex-col items-center">
              <div
                className="relative w-[180px] md:w-[220px] cursor-pointer group"
                onClick={() => handleVideoClick('/assets/営業時間.mp4')}
              >
                {/* Phone Frame */}
                <div className="relative bg-gray-900 rounded-[2rem] p-2 shadow-2xl group-hover:scale-105 transition-transform">
                  {/* Phone Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/3 h-5 bg-gray-900 rounded-b-xl z-10"></div>
                  {/* Phone Screen */}
                  <div className="relative rounded-[1.5rem] overflow-hidden bg-black aspect-[9/19]">
                    <video
                      src="/assets/営業時間.mp4"
                      className="w-full h-full object-contain"
                      autoPlay
                      loop
                      muted
                      playsInline
                    />
                    {/* Expand hint */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                      <MaterialIcon
                        name="fullscreen"
                        className="text-white text-3xl opacity-0 group-hover:opacity-100 transition-opacity"
                      />
                    </div>
                  </div>
                </div>
                {/* Decorative glow */}
                <div className="absolute -inset-4 bg-indigo-400/20 rounded-[3rem] blur-2xl -z-10"></div>
              </div>

              {/* Demo CTA under video */}
              <div className="mt-6 text-center">
                <p className="text-sm text-solution-text-secondary mb-2 flex items-center justify-center gap-1">
                  <MaterialIcon name="arrow_downward" className="text-indigo-500 text-base animate-bounce" />
                  実際にAIと会話してみよう
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  <SmartLink
                    href="kiosk-floating.html?zone=0"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-indigo-500 hover:bg-indigo-600 rounded-full text-white font-bold transition-all hover:scale-105 shadow-lg shadow-indigo-500/30"
                  >
                    <MaterialIcon name="smart_toy" className="text-lg" />
                    FAQ AI
                  </SmartLink>
                  <SmartLink
                    href="kiosk-floating.html?zone=3"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-500 hover:bg-green-600 rounded-full text-white font-bold transition-all hover:scale-105 shadow-lg shadow-green-500/30"
                  >
                    <MaterialIcon name="translate" className="text-lg" />
                    多言語
                  </SmartLink>
                </div>
              </div>
            </div>

            {/* Text Content */}
            <div className="order-2">
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-blue-100 text-blue-600 text-xs font-bold">
                  <MaterialIcon name="check" className="text-sm" />
                  FAQ運用過多を解決
                </span>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-solution-text mb-4">FAQ・施設案内AI</h3>
              <p className="text-solution-text-secondary mb-6 text-lg leading-relaxed">
                「トイレはどこ？」「営業時間は？」など、頻繁に寄せられる質問をAIが自動対応。スタッフは接客に集中できます。
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-solution-text">
                  <span className="size-6 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                    <MaterialIcon name="check" className="text-indigo-600 text-sm" />
                  </span>
                  AIが回答困難なら有人へ即座転送
                </li>
                <li className="flex items-center gap-3 text-solution-text">
                  <span className="size-6 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                    <MaterialIcon name="check" className="text-indigo-600 text-sm" />
                  </span>
                  LINE WORKS・電話連携
                </li>
                <li className="flex items-center gap-3 text-solution-text">
                  <span className="size-6 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
                    <MaterialIcon name="check" className="text-indigo-600 text-sm" />
                  </span>
                  自動学習で回答精度向上
                </li>
              </ul>
            </div>
          </div>

          {/* 機能2: フロアマップ・ナビゲーション（テキスト左、動画右） */}
          <div
            id="feature-floormap"
            className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center scroll-mt-20"
          >
            {/* Text Content */}
            <div className="order-2 lg:order-1">
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-red-100 text-red-600 text-xs font-bold">
                  <MaterialIcon name="check" className="text-sm" />
                  案内所負担を解決
                </span>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-solution-text mb-4">フロアマップ・ナビゲーション</h3>
              <p className="text-solution-text-secondary mb-6 text-lg leading-relaxed">
                現在地から目的地までの最短ルートを、3Dマップと直感的な矢印でご案内。「私のメガネが壊れちゃった」など曖昧な要望からも最適な店舗を提案します。
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-solution-text">
                  <span className="size-6 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
                    <MaterialIcon name="check" className="text-orange-600 text-sm" />
                  </span>
                  現在地からの最短ルート案内
                </li>
                <li className="flex items-center gap-3 text-solution-text">
                  <span className="size-6 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
                    <MaterialIcon name="check" className="text-orange-600 text-sm" />
                  </span>
                  エレベーター優先等のルート設定
                </li>
                <li className="flex items-center gap-3 text-solution-text">
                  <span className="size-6 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
                    <MaterialIcon name="check" className="text-orange-600 text-sm" />
                  </span>
                  曖昧な要望から店舗を提案
                </li>
              </ul>
            </div>

            {/* Video */}
            <div className="order-1 lg:order-2 flex flex-col items-center">
              <div
                className="relative w-[180px] md:w-[220px] cursor-pointer group"
                onClick={() => handleVideoClick('/assets/メガネ_push2talk.mp4')}
              >
                {/* Phone Frame */}
                <div className="relative bg-gray-900 rounded-[2rem] p-2 shadow-2xl group-hover:scale-105 transition-transform">
                  {/* Phone Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/3 h-5 bg-gray-900 rounded-b-xl z-10"></div>
                  {/* Phone Screen */}
                  <div className="relative rounded-[1.5rem] overflow-hidden bg-black aspect-[9/19]">
                    <video
                      src="/assets/メガネ_push2talk.mp4"
                      className="w-full h-full object-contain"
                      autoPlay
                      loop
                      muted
                      playsInline
                    />
                    {/* Expand hint */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                      <MaterialIcon
                        name="fullscreen"
                        className="text-white text-3xl opacity-0 group-hover:opacity-100 transition-opacity"
                      />
                    </div>
                  </div>
                </div>
                {/* Decorative glow */}
                <div className="absolute -inset-4 bg-orange-400/20 rounded-[3rem] blur-2xl -z-10"></div>
              </div>

              {/* Demo CTA under video */}
              <div className="mt-6 text-center">
                <p className="text-sm text-solution-text-secondary mb-2 flex items-center justify-center gap-1">
                  <MaterialIcon name="arrow_downward" className="text-orange-500 text-base animate-bounce" />
                  実際にAIと会話してみよう
                </p>
                <SmartLink
                  href="kiosk-floating.html?zone=1"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-orange-500 hover:bg-orange-600 rounded-full text-white font-bold transition-all hover:scale-105 shadow-lg shadow-orange-500/30"
                >
                  <MaterialIcon name="smart_toy" />
                  AIで道案内を体験
                </SmartLink>
              </div>
            </div>
          </div>

          {/* 機能3: リアルタイム会話（動画左、テキスト右） */}
          <div
            id="feature-realtime"
            className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center scroll-mt-20"
          >
            {/* Video */}
            <div className="order-1 lg:order-1 flex flex-col items-center">
              <div
                className="relative w-[180px] md:w-[220px] cursor-pointer group"
                onClick={() => handleVideoClick('/assets/メガネ_リアルタイム.mp4')}
              >
                {/* Phone Frame */}
                <div className="relative bg-gray-900 rounded-[2rem] p-2 shadow-2xl group-hover:scale-105 transition-transform">
                  {/* Phone Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-1/3 h-5 bg-gray-900 rounded-b-xl z-10"></div>
                  {/* Phone Screen */}
                  <div className="relative rounded-[1.5rem] overflow-hidden bg-black aspect-[9/19]">
                    <video
                      src="/assets/メガネ_リアルタイム.mp4"
                      className="w-full h-full object-contain"
                      autoPlay
                      loop
                      muted
                      playsInline
                    />
                    {/* Expand hint */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                      <MaterialIcon
                        name="fullscreen"
                        className="text-white text-3xl opacity-0 group-hover:opacity-100 transition-opacity"
                      />
                    </div>
                  </div>
                </div>
                {/* Decorative glow */}
                <div className="absolute -inset-4 bg-purple-400/20 rounded-[3rem] blur-2xl -z-10"></div>
              </div>

              {/* Demo CTA under video */}
              <div className="mt-6 text-center">
                <p className="text-sm text-solution-text-secondary mb-2 flex items-center justify-center gap-1">
                  <MaterialIcon name="arrow_downward" className="text-purple-500 text-base animate-bounce" />
                  実際にAIと会話してみよう
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  <a
                    href="https://gbase.ai/realtime/198db143-19fc-4997-a654-0c6d748e4b61"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white font-bold transition-all hover:scale-105 shadow-lg"
                    style={{
                      backgroundColor: '#a855f7',
                      boxShadow: '0 10px 15px -3px rgba(168, 85, 247, 0.3)',
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#9333ea')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#a855f7')}
                  >
                    <MaterialIcon name="mic" className="text-lg" />
                    リアルタイム会話
                  </a>
                  <SmartLink
                    href="kiosk-floating.html?zone=3"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-500 hover:bg-green-600 rounded-full text-white font-bold transition-all hover:scale-105 shadow-lg shadow-green-500/30"
                  >
                    <MaterialIcon name="translate" className="text-lg" />
                    多言語
                  </SmartLink>
                </div>
              </div>
            </div>

            {/* Text Content */}
            <div className="order-2 lg:order-2">
              <div className="flex items-center gap-3 mb-4">
                <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-orange-100 text-orange-600 text-xs font-bold">
                  <MaterialIcon name="check" className="text-sm" />
                  多言語対応を解決
                </span>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-solution-text mb-4">リアルタイム会話</h3>
              <p className="text-solution-text-secondary mb-6 text-lg leading-relaxed">
                キーボード入力は不要。話しかけるだけで、AIが意図を理解し的確に案内。高齢者や外国人観光客にも優しいインターフェースです。
              </p>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-solution-text">
                  <span className="size-6 rounded-full bg-grape-100 flex items-center justify-center shrink-0">
                    <MaterialIcon name="check" className="text-purple-600 text-sm" />
                  </span>
                  ハンズフリー操作
                </li>
                <li className="flex items-center gap-3 text-solution-text">
                  <span className="size-6 rounded-full bg-grape-100 flex items-center justify-center shrink-0">
                    <MaterialIcon name="check" className="text-purple-600 text-sm" />
                  </span>
                  騒がしい場所でも正確認識
                </li>
                <li className="flex items-center gap-3 text-solution-text">
                  <span className="size-6 rounded-full bg-grape-100 flex items-center justify-center shrink-0">
                    <MaterialIcon name="check" className="text-purple-600 text-sm" />
                  </span>
                  10言語以上のリアルタイム翻訳
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CoreFeaturesSection;
